// ─────────────────────────────────────────────────────────────────────────
// Deliberately different from CustodialView's src/firebase.js.
//
// CustodialView is one codebase deployed to N separate per-buyer Firebase
// projects, so its firebase.js fetches /__/firebase/init.json to pick up
// whichever project it's hosted on, and auth is a single anonymous session
// per project (safe there only because each buyer's data already lives in
// its own isolated project — Firestore rules just check request.auth !=
// null, see that repo's firestore.rules comments).
//
// This product is the opposite shape: ONE shared Firebase project serving
// every Edifice View customer, with Vendor Admins who must never see
// another vendor's data even though they're in the same database. That can
// only be enforced with real per-user identity (Firebase Auth
// email/password, one account per human) plus Firestore Security Rules
// that check a memberships/{uid} document for facilityId/vendorId/role —
// an anonymous shared session cannot do this safely. See README
// "The one architectural fact that matters most".
//
// Phase 2 adds one deliberate exception: field workers reached via a QR
// scan (src/screens/FieldFlow.jsx) get an anonymous session instead of a
// real account — mirrors CustodialView's own already-accepted security
// model for its team-member flows (any authenticated session can read/
// write; PIN-checking a worker's identity is a client-side UX concern,
// not a rules-enforced one). This is safe here specifically because the
// spec's one hard security requirement — Vendor Admins never seeing each
// other's *admin-level* data — is enforced entirely by the memberships/
// {uid}-based rules above, none of which anonymous sessions can satisfy
// (see firestore.rules' Phase 2 section comment for the full reasoning).
//
// Real project: `edifice-view` (see README "Firebase project"). Fill in
// its config in .env.local (gitignored; copy .env.local.example) to test
// locally — the fallback below only matters if that file is missing.
// ─────────────────────────────────────────────────────────────────────────

import { initializeApp } from "firebase/app";
import { getFirestore }  from "firebase/firestore";
import { getStorage }    from "firebase/storage";
import { getAuth, signInAnonymously } from "firebase/auth";

const config = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

export let db, storage, auth;

export async function initFirebase() {
  // Falls back to running without Firebase if .env.local is missing —
  // Phase 0's App.jsx doesn't touch db/storage/auth at all (it's a smoke
  // test against fake local state), so this lets it render instead of
  // throwing on a missing/invalid API key. Once Phase 1 wires App.jsx to
  // real data, .env.local (see README "Firebase project") is required.
  if (!config.apiKey) {
    console.warn(
      "No Firebase config — copy .env.local.example to .env.local and " +
      "fill in the edifice-view project's config (see README). Running " +
      "without Firebase for now."
    );
    return;
  }
  const app = initializeApp(config);
  db      = getFirestore(app);
  storage = getStorage(app);
  auth    = getAuth(app);
  // No signInAnonymously here on purpose — every ADMIN session must be a
  // real, identified user (see file header). Sign-in happens from the
  // login screen via signInWithEmailAndPassword. FieldFlow.jsx calls
  // signInAnonymouslyForField() itself, only on the QR-scan path.
}

export async function signInAnonymouslyForField() {
  if (!auth.currentUser) await signInAnonymously(auth);
  return auth.currentUser;
}
